import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmailTemplatesManager } from "@/components/EmailTemplatesManager";
import IntakeShareCard from "@/components/IntakeShareCard";
import { KeyRound, Loader2, ShieldCheck, Mail, BarChart3, Clock } from "lucide-react";

const IDLE_OPTIONS = [5, 10, 15, 30, 45, 60, 120];
import { toast } from "sonner";

const DAYS = [
  { v: "mon", l: "Monday" }, { v: "tue", l: "Tuesday" }, { v: "wed", l: "Wednesday" },
  { v: "thu", l: "Thursday" }, { v: "fri", l: "Friday" }, { v: "sat", l: "Saturday" }, { v: "sun", l: "Sunday" },
];

const DAY_IDX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

function localEquivalent(day, hour) {
  const now = new Date();
  const d = new Date(now);
  const diff = (DAY_IDX[day] - d.getUTCDay() + 7) % 7;
  d.setUTCDate(d.getUTCDate() + diff);
  d.setUTCHours(hour, 0, 0, 0);
  const wd = d.toLocaleDateString(undefined, { weekday: "short" });
  const t = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${wd} ${t}`;
}

const dayLabel = (v) => DAYS.find((d) => d.v === v)?.l || v;

export default function Settings() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [digestDay, setDigestDay] = useState("mon");
  const [digestHour, setDigestHour] = useState(8);
  const [savingDigest, setSavingDigest] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [idleTimeout, setIdleTimeout] = useState(15);
  const [savingSecurity, setSavingSecurity] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/digest-settings").then((r) => {
      setDigestEnabled(r.data.enabled);
      setDigestDay(r.data.day);
      setDigestHour(r.data.hour);
    }).catch(() => {});
    api.get("/admin/digest-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/settings").then((r) => setIdleTimeout(r.data.idle_timeout_minutes)).catch(() => {});
  }, [user]);

  const saveSecurity = async () => {
    setSavingSecurity(true);
    try {
      await api.put("/settings", { idle_timeout_minutes: Number(idleTimeout) });
      toast.success("Security settings saved — applies on next sign-in or page refresh");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save settings");
    } finally { setSavingSecurity(false); }
  };

  const loadAnalytics = () => api.get("/admin/digest-analytics").then((r) => setAnalytics(r.data)).catch(() => {});

  const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
    } finally { setSaving(false); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/send-digest");
      toast.success(data.sent ? "Weekly digest sent to your inbox" : "Email is disabled — nothing sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send digest");
    } finally { setSendingDigest(false); }
  };

  const saveDigestSettings = async () => {
    setSavingDigest(true);
    try {
      await api.put("/admin/digest-settings", { enabled: digestEnabled, day: digestDay, hour: Number(digestHour) });
      toast.success("Digest preferences saved");
      loadAnalytics();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save preferences");
    } finally { setSavingDigest(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-2xl" data-testid="settings-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900">Settings</h1>
      <p className="text-gray-500 text-sm mt-1 mb-7">Manage your account.</p>

      <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center font-extrabold text-lg">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{user?.name}</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-sm bg-gray-100 text-gray-600 capitalize">
            <ShieldCheck className="w-3.5 h-3.5" /> {user?.role}
          </span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 mb-5">
          <KeyRound className="w-4 h-4 text-gray-400" /> Change password
        </h3>
        {user?.is_sso ? (
          <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-sm p-3" data-testid="sso-password-notice">
            <svg width="18" height="18" viewBox="0 0 23 23" className="mt-0.5 shrink-0" aria-hidden="true"><path fill="#f25022" d="M1 1h10v10H1z"/><path fill="#7fba00" d="M12 1h10v10H12z"/><path fill="#00a4ef" d="M1 12h10v10H1z"/><path fill="#ffb900" d="M12 12h10v10H12z"/></svg>
            <span>Your sign-in is managed by <strong>Microsoft</strong>. Change your password through your Microsoft account — it can't be changed here.</span>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div>
            <label className={labelCls}>Current password</label>
            <input data-testid="current-password-input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>New password</label>
            <input data-testid="new-password-input" type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Confirm new password</label>
            <input data-testid="confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={inputCls} />
          </div>
          <button type="submit" disabled={saving} data-testid="change-password-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Update password
          </button>
        </form>
        )}
      </div>

      <IntakeShareCard />

      {user?.role === "admin" && (
        <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="security-settings-card">
          <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 mb-1.5">
            <Clock className="w-4 h-4 text-gray-400" /> Security
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Automatically sign users out after a period of inactivity (applies to everyone, org-wide).
          </p>
          <div className="max-w-xs">
            <label className={labelCls}>Auto-logout after inactivity</label>
            <select value={idleTimeout} onChange={(e) => setIdleTimeout(Number(e.target.value))} data-testid="idle-timeout-select" className={inputCls}>
              {IDLE_OPTIONS.map((m) => (
                <option key={m} value={m}>{m} minutes</option>
              ))}
            </select>
          </div>
          <button onClick={saveSecurity} disabled={savingSecurity} data-testid="save-security-button"
            className="mt-4 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {savingSecurity && <Loader2 className="w-4 h-4 animate-spin" />} Save security settings
          </button>
        </div>
      )}

      {user?.role === "admin" && <EmailTemplatesManager />}

      {user?.role === "admin" && (
        <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="admin-digest-card">
          <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 mb-1.5">
            <Mail className="w-4 h-4 text-gray-400" /> Weekly digest
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Get a summary of new clients, notes, uploads and active team members emailed to you.
            Choose if and when you'd like to receive it (times are UTC).
          </p>

          <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-4" data-testid="digest-enabled-toggle">
            <input type="checkbox" checked={digestEnabled} onChange={(e) => setDigestEnabled(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#002FA7]" data-testid="digest-enabled-checkbox" />
            <span className="text-sm">
              <span className="font-semibold text-gray-800">Email me the weekly digest</span>
              <span className="block text-xs text-gray-500">Turn off to stop receiving the digest.</span>
            </span>
          </label>

          <div className={`grid grid-cols-2 gap-3 mb-4 ${digestEnabled ? "" : "opacity-50 pointer-events-none"}`}>
            <div>
              <label className={labelCls}>Day</label>
              <select value={digestDay} onChange={(e) => setDigestDay(e.target.value)} data-testid="digest-day-select" className={inputCls}>
                {DAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Time (UTC)</label>
              <select value={digestHour} onChange={(e) => setDigestHour(Number(e.target.value))} data-testid="digest-hour-select" className={inputCls}>
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2 mb-4" data-testid="digest-local-time">
            ≈ <strong>{localEquivalent(digestDay, digestHour)}</strong> your local time
          </p>

          <div className="flex flex-wrap gap-2">
            <button onClick={saveDigestSettings} disabled={savingDigest} data-testid="save-digest-settings-button"
              className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
              {savingDigest && <Loader2 className="w-4 h-4 animate-spin" />} Save preferences
            </button>
            <button onClick={sendDigest} disabled={sendingDigest} data-testid="send-digest-button"
              className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
              {sendingDigest && <Loader2 className="w-4 h-4 animate-spin" />} Send me one now
            </button>
          </div>
        </div>
      )}

      {user?.role === "admin" && analytics && (
        <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="digest-analytics-card">
          <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-gray-400" /> Digest analytics (team)
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { label: "Admins", value: analytics.total_admins, testid: "stat-total-admins" },
              { label: "Subscribed", value: analytics.enabled, testid: "stat-enabled" },
              { label: "Opted out", value: analytics.disabled, testid: "stat-disabled" },
              { label: "Sent (7 days)", value: analytics.sent_last_7d, testid: "stat-sent-7d" },
            ].map((s) => (
              <div key={s.label} data-testid={s.testid} className="border border-gray-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-extrabold text-[#002FA7]">{s.value}</p>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  {["Admin", "Digest", "Schedule (UTC)", "Last sent"].map((h) => (
                    <th key={h} className="text-xs font-bold uppercase tracking-wider text-gray-500 py-2 pr-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="digest-recipients-body">
                {analytics.recipients.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100 last:border-0" data-testid={`digest-recipient-${r.id}`}>
                    <td className="py-2 pr-4">
                      <p className="text-sm font-semibold text-gray-900">{r.name}</p>
                      <p className="text-xs text-gray-500">{r.email}</p>
                    </td>
                    <td className="py-2 pr-4">
                      {r.enabled
                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-green-50 text-green-700 border border-green-200">On</span>
                        : <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-gray-100 text-gray-500 border border-gray-200">Off</span>}
                    </td>
                    <td className="py-2 pr-4 text-sm text-gray-600">{r.enabled ? `${dayLabel(r.day)} ${String(r.hour).padStart(2, "0")}:00` : "—"}</td>
                    <td className="py-2 pr-4 text-sm text-gray-600">{r.last_digest_sent || "Never"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
